import { Fragment, type ReactNode } from 'react';

import { ThreadPrimitive } from '@assistant-ui/react';
import * as m from 'motion/react-m';
import { useTranslations } from 'next-intl';

import { useConfig } from '@/components/providers/config-provider';
import { Button } from '@/components/ui/button';
import { DynamicIcon } from '@/components/ui/dynamic-icon';
import { useLocalizedConfig } from '@/hooks/use-localized-config';
import { useRandomizedSuggestions } from '@/hooks/use-randomized-suggestions';
import { useRequestStorageAccess } from '@/lib/storage/use-storage-access';
import { ChatSuggestion, WelcomeAlignment } from '@/lib/types/tenant';
import { cn } from '@/lib/utils';

const ALIGNMENT_CLASSES: Record<WelcomeAlignment, string> = {
  start: 'items-start text-start',
  center: 'items-center text-center',
  end: 'items-end text-end',
};

const VERTICAL_ALIGN_CLASSES: Record<WelcomeAlignment, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
};

const SELF_ALIGN_CLASSES: Record<WelcomeAlignment, string> = {
  start: 'self-start',
  center: 'self-center',
  end: 'self-end',
};

function parseLineBreaks(text: string): ReactNode {
  const parts = text.split(/<br\s*\/?>/gi);
  if (parts.length === 1) return text;

  return parts.map((part, i) => (
    <Fragment key={i}>
      {part}
      {i < parts.length - 1 && <br />}
    </Fragment>
  ));
}

export function ThreadWelcome() {
  const t = useTranslations();
  const { branding, assetBasePath } = useConfig();
  const welcomeTitle = useLocalizedConfig<string>('chat.welcomeTitle');
  const welcomeSubtitle = useLocalizedConfig<string>('chat.welcomeSubtitle');

  const welcomeConfig = branding?.components?.welcome;
  const welcomeImage = welcomeConfig?.image;
  const imageSrc =
    welcomeImage?.path && assetBasePath ? `${assetBasePath}/${welcomeImage.path}` : null;

  return (
    <div className="aui-thread-welcome-root flex w-full flex-grow flex-col">
      <div className="aui-thread-welcome-center flex w-full flex-grow flex-col items-center justify-center">
        <div
          className={cn(
            'aui-thread-welcome-message flex size-full flex-col px-4 py-6',
            welcomeConfig?.verticalAlign
              ? VERTICAL_ALIGN_CLASSES[welcomeConfig.verticalAlign]
              : 'justify-center',
            welcomeConfig?.align && ALIGNMENT_CLASSES[welcomeConfig.align],
          )}
        >
          <m.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="aui-thread-welcome-message-motion-1 text-(length:--welcome-title-font-size,24px) font-(--welcome-title-font-weight,600)"
          >
            {welcomeTitle ? parseLineBreaks(welcomeTitle) : t('thread.welcomeTitle')}
          </m.h1>
          <m.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ delay: 0.1 }}
            className="aui-thread-welcome-message-motion-2 text-(length:--welcome-subtitle-font-size,24px) font-(--welcome-subtitle-font-weight,400) text-(--welcome-subtitle-color,var(--muted-foreground)/65)"
          >
            {welcomeSubtitle ? parseLineBreaks(welcomeSubtitle) : t('thread.welcomeSubtitle')}
          </m.p>
          {imageSrc && (
            <m.img
              src={imageSrc}
              alt={welcomeImage?.alt ?? ''}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ delay: 0.2 }}
              className={cn(
                'aui-thread-welcome-image mt-4',
                SELF_ALIGN_CLASSES[welcomeImage?.align ?? 'center'],
              )}
              style={welcomeImage?.style}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export function ThreadWelcomeSuggestions() {
  const rawSuggestions = useLocalizedConfig<ChatSuggestion[]>('chat.suggestions');
  const displayedSuggestions = useRandomizedSuggestions(rawSuggestions);
  const { requestOnInteraction } = useRequestStorageAccess();

  if (!displayedSuggestions.length) {
    return null;
  }

  return (
    <div className="aui-thread-welcome-suggestions mt-auto grid w-full auto-rows-max gap-3 px-4 pb-4 @xl:grid-cols-2">
      {displayedSuggestions.map((suggestedAction: ChatSuggestion, index: number) => {
        const { action, title, label, icon } = suggestedAction;
        const prompt = action || title;

        return (
          <m.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ delay: 0.05 * index }}
            key={title}
            className="aui-thread-welcome-suggestion-display grid [&:nth-child(n+3)]:hidden @xl:[&:nth-child(n+3)]:block @xl:[&:nth-child(n+5)]:hidden @4xl:[&:nth-child(n+5)]:block"
          >
            <ThreadPrimitive.Suggestion prompt={prompt} send asChild>
              <Button
                variant="ghost"
                onClick={requestOnInteraction}
                className="aui-thread-welcome-suggestion flex h-full w-full flex-col items-start justify-center gap-1 rounded-3xl border border-border px-6 py-4 text-start text-sm dark:hover:bg-accent/60"
                aria-label={prompt}
              >
                <span className="aui-thread-welcome-suggestion-text-1 flex w-full items-center gap-2 leading-relaxed font-medium whitespace-normal">
                  {icon && <DynamicIcon name={icon} className="size-4 shrink-0" />}
                  {title}
                </span>
                {label && (
                  <span className="aui-thread-welcome-suggestion-text-2 overflow-wrap-anywhere w-full leading-relaxed break-words whitespace-normal text-muted-foreground">
                    {label}
                  </span>
                )}
              </Button>
            </ThreadPrimitive.Suggestion>
          </m.div>
        );
      })}
    </div>
  );
}
